import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from 'react-router-dom';
import { Tabs, TabsContent, TabsList, TabsTrigger } from 'components/ui/tabs';
import { Button } from 'components/ui/button';
import { LoaderCircle, Trash2 } from 'lucide-react';
import { FileManagerMyFiles } from "../file-manager/my-files"
import { SharedWithMe } from "../file-manager/shared-files"
import Trash from "../file-manager/trash"
import { useSubscriptionStore } from 'state/store/subscription';
import { useSubscribeFreePlan } from 'features/subscription/hooks/use-subscription';
import { WelcomeDialog } from 'components/blocks/welcome-dialog/welcome-dialog';
import { initializeCurrentPlan } from 'features/subscription/services/subscription-service';
import { useQueryClient } from '@tanstack/react-query';
import { useConfig } from 'hooks/use-config';
import { PLANS } from 'constant/plan';
import { useSecureConfigStore } from 'state/store/config/useConfigStore';
import { useIsMobile } from 'hooks/use-mobile';
import AddDropdownMenu from "features/file-manager/components/file-manager-add-new-dropdown";
import { useFileManager } from "features/file-manager/hooks/use-file-manager";
import ConfirmationModal from "components/blocks/confirmation-modal/confirmation-modal";
import { useTheme } from 'components/core/theme-provider';

export default function DocumentManagementPage() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const isMobile = useIsMobile();
    const [tabId, setTabId] = useState('myFiles');
    const [showWelcomeDialog, setShowWelcomeDialog] = useState(false);
    const [hasShownWelcome, setHasShownWelcome] = useState(false);
    const fileManager = useFileManager({ onCreateFile: () => setTabId('myFiles'), defaultViewMode: 'list' });
    // Check current subscription plan
    const { currentPlan, isLoading: isCurrentPlanLoading, setCurrentPlan, setLoading, setError } = useSubscriptionStore();
    const queryClient = useQueryClient();
    const { configurationSettings } = useConfig();
    const { siteConfigurationSettings } = useSecureConfigStore();
    const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
    const clearTrashRef = React.useRef<(() => Promise<void>) | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [allFileIds, setAllFileIds] = React.useState<string[]>([]);
    const { theme } = useTheme();

    // Subscribe to free plan mutation
    const { mutate: subscribeToFreePlan, isPending: isSubscribing } = useSubscribeFreePlan({
        onSuccess: async (data) => {
            if (data.isSuccess && !hasShownWelcome) {
                // Refresh the current plan data to update the store
                await initializeCurrentPlan(configurationSettings, setCurrentPlan, setLoading, setError);

                // Invalidate any cached queries to ensure fresh data
                await queryClient.invalidateQueries({ queryKey: ['current-plan'] });

                // Show welcome dialog after successful subscription (only once)
                setShowWelcomeDialog(true);
                setHasShownWelcome(true);

                // The component will automatically re-render and show the document manager
                // since currentPlan will no longer be null
            }
        },
        onError: (error) => {
            console.error('Failed to subscribe to free plan:', error);
            setShowWelcomeDialog(true);
        }
    });

    const handleFileUpload = async (files: File[]) => {
        await fileManager?.handleFileUpload(files);
    };

    const handleFolderCreate = async (folderName: string) => {
        await fileManager?.handleFolderCreate(folderName);
    };

    const handleClearTrash = () => {
        setIsConfirmationModalOpen(true);
    };

    const handleConfirmClearTrash = async () => {
        if (clearTrashRef.current) {
            try {
                setIsLoading(true);
                await clearTrashRef.current();
                setIsConfirmationModalOpen(false);
            } catch (error) {
                console.error('Clear trash failed:', error);
            } finally {
                setIsLoading(false);
            }
        }
    };

    // Subscription prompt component for document manager
    const SubscriptionPrompt = () => {
        const handleViewAllPlans = () => {
            navigate('/subscription', {
                state: { from: '/document-manager' }
            });
        };

        const handleStartFreeTrial = () => {
            subscribeToFreePlan();
        };

        return (
            <div className="flex flex-col justify-center items-center w-full bg-background px-4 py-16">
                <div className="max-w-2xl text-center space-y-8">
                    {/* Main heading */}
                    <div className="space-y-4">
                        <h1 className="text-3xl md:text-3xl font-bold text-foreground leading-tight">
                            {t('UNLOCK_DOCUMENT_MANAGEMENT')}
                        </h1>
                        <h2 className="text-3xl md:text-3xl font-bold text-foreground">
                            {t('ORGANIZE_YOUR_FILES_WITH_AI')}
                        </h2>
                    </div>

                    {/* Description */}
                    <p className="text-sm text-muted-foreground leading-relaxed max-w-xl mx-auto">
                        {t('ACCESS_POWERFUL_DOCUMENT_FEATURES')}
                    </p>

                    {/* Action buttons */}
                    <div className="flex flex-col sm:flex-row gap-4 justify-center items-center pt-6">
                        <Button
                            onClick={handleViewAllPlans}
                            className="bg-primary hover:bg-primary text-white px-8 py-3 rounded-lg font-semibold min-w-[140px]"
                        >
                            {t('VIEW_ALL_PLANS')}
                        </Button>
                        {!(currentPlan?.name === PLANS.NOPLAN) && (
                            <Button
                                onClick={handleStartFreeTrial}
                                variant="outline"
                                className="border-2 border-border hover:bg-accent px-8 py-3 rounded-lg font-semibold min-w-[140px]"
                                disabled={isSubscribing}
                            >
                                {isSubscribing ? t('STARTING_TRIAL') + '...' : t('START_FREE_TRIAL')}
                            </Button>
                        )}
                    </div>
                </div>
            </div>
        );
    };

    if (isCurrentPlanLoading) {
        return (
            <div className="flex flex-col justify-center items-center w-full h-full">
                <LoaderCircle className="text-primary animate-spin" />
            </div>
        );
    }

    // Check if subscription info should be shown (only show if explicitly true)
    const showSubscriptionInfo = siteConfigurationSettings?.siteConfigurations?.data?.showSubscriptionInfo === true;

    // Show subscription prompt if no plan and subscription info is enabled
    if (!currentPlan && showSubscriptionInfo) {
        return <SubscriptionPrompt />;
    }

    return (
        <div className="flex flex-col h-full overflow-hidden w-full">
            <div className="h-full flex-col flex w-full ">
                <h2 className="text-2xl font-bold tracking-tight">{t('DOCUMENT_MANAGEMENT')}</h2>

                <div className="flex flex-col w-full gap-2">
                    <Tabs value={tabId}>
                        <div className="my-3 sm:my-5 flex items-center justify-between rounded text-base">
                            <TabsList className="border border-primary p-1 rounded-lg font-medium">
                                <TabsTrigger
                                    onClick={() => setTabId('myFiles')}
                                    value="myFiles"
                                    className={`data-[state=active]:bg-primary-600 ${theme === 'dark' ? 'data-[state=active]:text-neutral-800 text-primary-300' : 'data-[state=active]:text-neutral-50 text-neutral-800'} rounded-lg px-[12px] py-[6px]`}
                                >
                                    {t('MY_FILES')}
                                </TabsTrigger>
                                <TabsTrigger
                                    onClick={() => setTabId('sharedWithMe')}
                                    value="sharedWithMe"
                                    className={`data-[state=active]:bg-primary-600 ${theme === 'dark' ? 'data-[state=active]:text-neutral-800 text-primary-300' : 'data-[state=active]:text-neutral-50 text-neutral-800'} rounded-lg px-[12px] py-[6px]`}
                                >
                                    {t('SHARED_WITH_ME')}
                                </TabsTrigger>
                                <TabsTrigger
                                    onClick={() => setTabId('trash')}
                                    value="trash"
                                    className={`data-[state=active]:bg-primary ${theme === 'dark' ? 'data-[state=active]:text-neutral-800 text-primary-300' : 'data-[state=active]:text-neutral-50 text-neutral-800'} rounded-lg px-[12px] py-[6px]`}
                                >
                                    {t('TRASH')}
                                </TabsTrigger>
                            </TabsList>

                            {/* Upload File Button - Only show for myFiles tab */}
                            {tabId === 'myFiles' && (
                                <AddDropdownMenu
                                    onFileUpload={handleFileUpload}
                                    onFolderCreate={handleFolderCreate}
                                    isMobile={isMobile}
                                    isButtonHide={true}
                                />
                            )}

                            {tabId === 'trash' && (
                                <Button
                                    size="sm"
                                    variant="outline"
                                    className="pl-4 pr-6 py-[7px] text-sm font-bold disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer h-[38px]"
                                    onClick={handleClearTrash}
                                    disabled={!allFileIds.length}
                                >
                                    <Trash2 className="h-4 w-4" />
                                    {!isMobile && <span className="">{t('CLEAR_TRASH')}</span>}
                                </Button>
                            )}

                        </div>

                        <TabsContent value="myFiles">
                            <FileManagerMyFiles defaultViewMode="list" showTitle={false} />
                        </TabsContent>

                        <TabsContent value="sharedWithMe">
                            <SharedWithMe showTitle={false} />
                        </TabsContent>
                        <TabsContent value="trash">
                            <Trash
                                showTitle={false}
                                onClearTrashReady={(handler) => {
                                    clearTrashRef.current = handler;
                                }}
                                onAllFileIdsChange={(ids) => {
                                    setAllFileIds(ids);
                                    console.log('All file IDs in trash:', ids);
                                }}
                            />
                        </TabsContent>
                    </Tabs>
                </div>
            </div>

            {/* Welcome Dialog */}
            <WelcomeDialog
                open={showWelcomeDialog}
                onOpenChange={setShowWelcomeDialog}
                onGetStarted={() => {
                    // Stay on document manager after welcome
                    setShowWelcomeDialog(false);
                    window.location.reload();
                }}
            />

            <ConfirmationModal
                open={isConfirmationModalOpen}
                onOpenChange={setIsConfirmationModalOpen}
                title={t('CLEAR_TRASH')}
                description={t('CLEAR_TRASH_DESCRIPTION')}
                onConfirm={handleConfirmClearTrash}
                preventAutoClose={true}
                confirmText={t('CONFIRM')}
                cancelText={t('CANCEL')}
                isLoading={isLoading}
            />
        </div>
    );
}